// src/pages/monitor/FieldCompletionCard.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Field-level completion card for a selected job in the Job Monitor.
//
// Job-type-agnostic: it asks the backend for per-stage field stats and shows the
// stage matching THIS job's type. If the job type has no field-stats stage
// registered (or the stage hasn't run for this entity), the card renders nothing.
//
// Data source: GET /adriva_api/job/field-stats/{app}/{entity}
//   → { success, stages: { <JOB_TYPE>: StageFieldStats | null, ... } }
// Stats blocks are written by the pipelines as `_field_stats` on each entity
// result (see dg_field_stats.py / dg_pipeline_service edits).
//
// Resume path: the EXISTING adriva_runPipeline(..., resume=true), scoped to this
// one job_type. Handlers skip done fields and the pipeline's field-level sweep
// completes the remainder — so re-running processes ONLY the missing fields.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from "react";
import { T } from "../theme/theme";
import { adriva_getFieldStats, adriva_runPipeline, StageFieldStats } from "../API";

interface Props {
  job: any;                 // selected job row/detail (needs job_type, entity_id/dm_id, process_id)
  appNm: string;
  onResumed?: () => void;   // parent refreshes the job list after a resume kicks off
}

export default function FieldCompletionCard({ job, appNm, onResumed }: Props) {
  const jobType   = (job?.job_type || "").toUpperCase();
  const entityId  = job?.entity_id || job?.dm_id || "";
  const processId = job?.process_id || "";
  const status    = (job?.status || "").toLowerCase();
  const isRunning  = ["running", "started", "in_progress", "yet_to_start", "queued", "pending"].includes(status);
  const isStuck    = ["running", "started", "in_progress"].includes(status) && !!job?.end_time; // ran to "100%" but never flipped
  const canResume  = !isRunning || isStuck;   // terminal, or stuck (owner is effectively dead)
  const agentRecovered = job?.agent_recovered || 0

  // undefined = loading · null = no stage for this job type (hide) · object = show
  const [stat, setStat]         = useState<StageFieldStats | null | undefined>(undefined);
  const [error, setError]       = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);

  const load = useCallback(() => {
    if (!appNm || !entityId || !jobType) { setStat(null); return; }
    setStat(undefined);
    setError(null);
    adriva_getFieldStats(appNm, entityId)
      .then(res => setStat(res?.success ? (res.stages[jobType] ?? null) : null))
      .catch(e => setError(e?.message || "Could not load field stats"));
  }, [appNm, entityId, jobType]);

  useEffect(() => { load(); }, [load]);

  const handleResume = async () => {
    if (!entityId || !processId) return;
    setResuming(true);
    try {
      // Same process_id + this job_type + resume=true → handler skips done fields,
      // sweep completes the remainder. Field-scoped, idempotent.
      await adriva_runPipeline(appNm, entityId, processId, [jobType], true);
      onResumed?.();
      setTimeout(load, 2500); // let the backend write, then refresh the card
    } catch (e: any) {
      setError(e?.message || "Resume failed");
    } finally {
      setResuming(false);
    }
  };

  // ── Guards (order matters) ──────────────────────────────────────────────────
  // null → not applicable to this job type / stage not present → render nothing.
  if (stat === null) return null;

  const wrap: React.CSSProperties = {
    background: T.bgCard, border: `1px solid ${T.border}`,
    borderRadius: 9, padding: "14px 16px", marginBottom: 16,
  };
  const title: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
    color: T.brand, marginBottom: 12, display: "flex", alignItems: "center", gap: 8,
  };

  // error BEFORE loading, otherwise a failed fetch (stat still undefined) spins forever.
  if (error) {
    return (
      <div style={wrap}>
        <div style={title}>Field completion</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: T.red }}>⚠ {error}</span>
          <button
            onClick={load}
            style={{
              all: "unset", cursor: "pointer", fontSize: 11, fontWeight: 600,
              color: T.brand, background: T.brandLight, border: `1px solid ${T.brandMid}`,
              padding: "3px 9px", borderRadius: 5,
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (stat === undefined) {
    return (
      <div style={wrap}>
        <div style={title}>Field completion</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.textMuted, fontSize: 12 }}>
          <span style={{
            width: 14, height: 14, borderRadius: "50%",
            border: `2px solid ${T.border}`, borderTopColor: T.brand,
            animation: "spin .7s linear infinite", display: "inline-block",
          }} />
          Loading field stats…
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── stat is a StageFieldStats object from here ──────────────────────────────
  const complete   = stat.still_missing === 0;
  const barColor   = complete ? T.green : stat.pct >= 80 ? T.amber : T.red;
  const denomKnown = stat.present; // false = older run without a requested denominator

  return (
    <div style={wrap}>
      <div style={title}>
        Field completion
        <span style={{ fontWeight: 500, color: T.textMuted, textTransform: "none", letterSpacing: 0 }}>
          {stat.label}
        </span>
      </div>

      {/* headline numbers */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span style={{
          fontFamily: "'IBM Plex Mono',monospace", fontSize: 26, fontWeight: 800,
          lineHeight: 1, color: barColor,
        }}>
          {denomKnown ? `${stat.pct}%` : stat.completed}
        </span>
        <span style={{ fontSize: 12, color: T.textMuted }}>
          {denomKnown
            ? `${stat.completed} / ${stat.requested} fields complete`
            : `${stat.completed} fields (older run — denominator unknown)`}
        </span>
      </div>

      {/* progress bar */}
      {denomKnown && (
        <div style={{ height: 7, borderRadius: 99, background: T.border, overflow: "hidden", marginBottom: 8 }}>
          <div style={{ height: "100%", width: `${stat.pct}%`, background: barColor, borderRadius: 99, transition: "width .4s" }} />
        </div>
      )}

      {/* status chips */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11, color: T.textMuted, marginBottom: complete ? 0 : 12 }}>
        {stat.still_missing > 0 && (
          <span style={{ color: T.red, fontWeight: 600 }}>● {stat.still_missing} remaining</span>
        )}
        {stat.sweep_triggered && (
          <span style={{ color: T.amber }}>↻ auto-sweep ran{stat.recovered > 0 ? ` · recovered ${stat.recovered}` : ""}</span>
        )}
        {complete && <span style={{ color: T.green, fontWeight: 600 }}>✓ all fields complete</span>}
      </div>

 
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11, color: T.textMuted, marginBottom: complete && !canResume ? 0 : 12 }}>
        {agentRecovered > 0 && (
          <span style={{ color: T.amber }}>↻ agent recovered {agentRecovered}</span>
        )}
        {stat.sweep_triggered && (
          <span style={{ color: T.amber }}>↻ sweep recovered {stat.recovered}</span>
        )}
        {stat.still_missing > 0 && (
          <span style={{ color: T.red, fontWeight: 600 }}>● {stat.still_missing} still missing</span>
        )}
        {complete && agentRecovered === 0 && !stat.sweep_triggered && (
          <span style={{ color: T.green, fontWeight: 600 }}>✓ all fields complete</span>
        )}
        {complete && (agentRecovered > 0 || stat.sweep_triggered) && (
          <span style={{ color: T.green, fontWeight: 600 }}>✓ complete after recovery</span>
        )}
      </div>

      {/* Stuck job detection banner */}
      {stat.possibly_stuck && (
        <div style={{
          padding: "12px 16px", marginTop: 10,
          background: "#fff7ed", border: "1px solid #fde68a", borderRadius: 8,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 2 }}>
              Job appears stuck
            </div>
            <div style={{ fontSize: 11, color: "#b45309" }}>
              No progress update for {Math.round((stat.last_activity_ago_sec || 0) / 60)} minutes
              (threshold: {Math.round((stat.stuck_threshold_sec || 300) / 60)}m)
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={handleResume}
              disabled={resuming}
              style={{
                all: "unset", cursor: resuming ? "not-allowed" : "pointer",
                fontSize: 11, fontWeight: 700, color: "#0f766e",
                background: "#f0fdfa", padding: "5px 12px",
                borderRadius: 6, border: "1px solid #5eead4",
              }}
            >
              {resuming ? "Resuming…" : "⏭ Resume from stuck point"}
            </button>
          </div>
        </div>
      )}

      
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}